import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import api from '../lib/api'
import VehicleCard from '../components/VehicleCard'
import UpgradeModal from '../components/UpgradeModal'
import OverLimitModal from '../components/OverLimitModal'

export default function DashboardPage() {
  const navigate = useNavigate()
  const [dashboard, setDashboard] = useState(null)
  const [loading, setLoading] = useState(true)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [overLimitOpen, setOverLimitOpen] = useState(false)

  const fetchDashboard = () => {
    api.get('/dashboard')
      .then((res) => setDashboard(res.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchDashboard() }, [])

  useEffect(() => {
    if (dashboard?.over_vehicle_limit && !sessionStorage.getItem('overLimitDismissed')) {
      setOverLimitOpen(true)
    }
  }, [dashboard])

  const handleDismissOverdue = async (e, entryId) => {
    e.preventDefault()
    await api.patch(`/service_entries/${entryId}/dismiss_overdue`)
    fetchDashboard()
  }

  if (loading) {
    return <div className="text-center text-gray-500 py-12">Loading...</div>
  }

  const atLimit = dashboard && !dashboard.premium && dashboard.total_vehicles >= dashboard.vehicle_limit

  const handleAddVehicle = () => {
    if (atLimit) {
      setUpgradeOpen(true)
    } else {
      navigate('/vehicles/new')
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl font-extrabold text-ink">Your garage</h1>
        <button
          onClick={handleAddVehicle}
          className="px-4 py-2.5 text-sm font-bold text-white bg-accent hover:bg-accent-dark whitespace-nowrap"
        >
          + Add vehicle
        </button>
      </div>

      {dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-4 border-y-[1.5px] border-ink mb-8">
          {[
            ['Vehicles', dashboard.total_vehicles, false],
            ['Service visits', dashboard.total_records, false],
            ['Total spent', `$${dashboard.total_maintenance_cost?.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, false],
            ['Overdue', dashboard.overdue_count, dashboard.overdue_count > 0],
          ].map(([label, value, alert], i) => (
            <div
              key={label}
              className={`px-4 py-3 sm:py-4 border-line ${i % 2 === 0 ? 'border-r' : 'sm:border-r'} ${i < 2 ? 'border-b sm:border-b-0' : ''} ${i === 3 ? 'sm:border-r-0' : ''}`}
            >
              <p className="text-[11px] font-bold uppercase tracking-widest text-muted">{label}</p>
              <p className={`mt-1 font-mono text-xl sm:text-2xl font-medium ${alert ? 'text-accent' : 'text-ink'}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {dashboard?.overdue_items?.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-extrabold text-ink mb-3">Overdue services</h2>
          <div className="border-[1.5px] border-accent bg-surface divide-y divide-line">
            {dashboard.overdue_items.map((item) => (
              <Link
                key={item.id}
                to={`/vehicles/${item.vehicle_id}`}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4 px-4 py-3 hover:bg-white transition-colors"
              >
                <div className="min-w-0">
                  <span className="font-bold text-ink">{item.title}</span>
                  <span className="text-sm text-muted ml-2">{item.vehicle_name}</span>
                </div>
                <div className="flex items-center justify-between sm:justify-end gap-4">
                  <span className="font-mono text-xs text-muted">
                    {item.next_service_date && `Due ${new Date(item.next_service_date + 'T00:00:00').toLocaleDateString()}`}
                    {item.next_service_date && item.next_service_meter && ' / '}
                    {item.next_service_meter && `${item.next_service_meter.toLocaleString()} ${item.meter_label}`}
                  </span>
                  <button
                    onClick={(e) => handleDismissOverdue(e, item.id)}
                    className="text-sm font-semibold text-muted hover:text-ink py-1"
                  >
                    Dismiss
                  </button>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {dashboard?.vehicles?.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {dashboard.vehicles.map((vehicle) => (
            <VehicleCard key={vehicle.id} vehicle={vehicle} />
          ))}
        </div>
      ) : (
        <div className="border-[1.5px] border-dashed border-line py-12 px-4 text-center">
          <p className="text-muted mb-4">Nothing in the garage yet. Add your first vehicle to start the log.</p>
          <button
            onClick={handleAddVehicle}
            className="px-4 py-2.5 text-sm font-bold text-white bg-accent hover:bg-accent-dark"
          >
            + Add vehicle
          </button>
        </div>
      )}

      <UpgradeModal
        isOpen={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        reason="add more vehicles"
      />

      <OverLimitModal
        isOpen={overLimitOpen}
        onClose={() => { setOverLimitOpen(false); sessionStorage.setItem('overLimitDismissed', '1') }}
      />
    </div>
  )
}
